import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminTransactions, getAdminTransactionDetail } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const STATUS_COLOR = { success: "#2ec4b6", failed: "#e63946", abandoned: "#f4a261", pending: "#f4a261" };

export default function AllTransactions() {
  const dispatch = useDispatch();
  const transactions = useSelector((s) => s.profile.adminTransactions);
  const meta = useSelector((s) => s.profile.adminTransactionsMeta);
  const txDetail = useSelector((s) => s.profile.adminTxDetail);

  const [page, setPage] = useState(1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const load = (p = page) => {
    const params = { page: p, perPage: 50 };
    if (from) params.from = from;
    if (to) params.to = to;
    if (status) params.status = status;
    dispatch(getAdminTransactions(params));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [dispatch]);

  const openDetail = (tx) => {
    setSelected(tx);
    dispatch(getAdminTransactionDetail(tx.id));
  };

  const fmtMoney = (k) => k != null ? `₦${(k / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>All Transactions</h2>
          <p style={S.headingSub}>{meta?.total || transactions.length} transactions on Paystack</p>
        </div>
        <span style={S.countBadge}>{meta?.total || transactions.length}</span>
      </div>

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>From</label>
          <input style={S.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>To</label>
          <input style={S.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>Status</label>
          <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="abandoned">Abandoned</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
          <button style={S.btn} onClick={() => { setPage(1); load(1); }}>Apply Filter</button>
          <button style={S.btnSecondary} onClick={() => { setFrom(""); setTo(""); setStatus(""); setPage(1); dispatch(getAdminTransactions({ page: 1, perPage: 50 })); }}>Reset</button>
        </div>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Reference", "Amount", "Channel", "Status", "Customer", "Date"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No transactions found</td></tr>
            ) : transactions.map((tx) => (
              <tr key={tx.id || tx.reference} style={S.tr} onClick={() => openDetail(tx)}>
                <td style={S.td}><span style={S.mono}>{tx.reference}</span></td>
                <td style={{ ...S.td, fontWeight: 700, color: BG }}>{fmtMoney(tx.amount)}</td>
                <td style={S.td}>{tx.channel || "—"}</td>
                <td style={S.td}>
                  <span style={{ background: (STATUS_COLOR[tx.status] || "#888") + "18", color: STATUS_COLOR[tx.status] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {tx.status}
                  </span>
                </td>
                <td style={S.td}>{tx.customer?.email || "—"}</td>
                <td style={{ ...S.td, color: "#888" }}>{fmtDate(tx.paid_at || tx.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}>‹ Previous</button>
        <span style={{ color: "#888", fontSize: 13 }}>Page {page}</span>
        <button style={S.pgBtn} onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next ›</button>
      </div>

      {/* Detail modal */}
      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <span style={S.modalTitle}>Transaction Detail</span>
              <button style={S.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {[
                ["Reference", txDetail?.reference || selected.reference],
                ["Amount", fmtMoney(txDetail?.amount ?? selected.amount)],
                ["Status", txDetail?.status || selected.status],
                ["Channel", txDetail?.channel || selected.channel],
                ["Currency", txDetail?.currency || selected.currency],
                ["Customer Email", txDetail?.customer?.email || selected.customer?.email],
                ["Customer Name", txDetail?.customer?.name || selected.customer?.name],
                ["Paid At", fmtDate(txDetail?.paid_at || selected.paid_at)],
                ["IP Address", txDetail?.ip_address || "—"],
                ["Gateway Response", txDetail?.gateway_response || "—"],
              ].map(([k, v]) => (
                <div key={k} style={S.detailRow}>
                  <span style={S.detailKey}>{k}</span>
                  <span style={S.detailVal}>{v || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  countBadge: { background: "rgba(167,204,72,0.12)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700 },
  filterBar: { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20, alignItems: "flex-end" },
  filterGroup: { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  select: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13 },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 680 },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5", cursor: "pointer" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  mono: { fontFamily: "monospace", fontSize: 12, color: "#888" },
  pagination: { display: "flex", alignItems: "center", gap: 16, marginTop: 16, justifyContent: "flex-end" },
  pgBtn: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 18px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" },
  modal: { background: "#fff", borderRadius: 12, width: 480, maxWidth: "95vw", maxHeight: "80vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f0f0f0" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: BG },
  closeBtn: { background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer" },
  modalBody: { padding: "16px 20px" },
  detailRow: { display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #f5f5f5" },
  detailKey: { color: "#888", fontSize: 13 },
  detailVal: { color: BG, fontSize: 13, fontWeight: 500, maxWidth: "60%", textAlign: "right", wordBreak: "break-all" },
};
